import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import NumberFormat from 'react-number-format';
import { isEmpty } from 'lodash';
import { nanoid } from '@reduxjs/toolkit';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
  Typography,
} from '@material-ui/core';
import { wnUserDietProfileSelector } from 'features/user/selectors/user.selector';
import { useUpdateProfile } from 'hooks/user.hook';
import { SupplementsTable } from 'components/Tables/supplements/supplements-table.component';
import { generateTheme } from 'utils/helpers';
import { useStyles } from './diet-profile-page.styles';

const DIETS = [
  'Cetogénica',
  'Basada en plantas',
  'FODMAP',
  'Kosher',
  'Libre de gluten',
  'Paleolítica',
  'Pesceteriana',
  'Vegana',
  'Vegetariana',
].toSorted((a, b) => a.localeCompare(b));

const ALLERGIES = [
  'Lácteos',
  'Leche',
  'Gluten',
  'Mariscos',
  'Nueces',
  'Semillas',
  'Almendras',
  'Maní (cacahuate)',
  'Camarón',
  'Soya',
  'Fresas',
].toSorted((a, b) => a.localeCompare(b));

export const INTOLERANCES = [
  'Vegetariano',
  'Sin gluten',
  'Sin huevo',
  'Sin lácteos',
  'Sin leguminosas',
  'Sin granos',
  'Sin azúcar',
].toSorted((a, b) => a.localeCompare(b));

export const DietProfilePage = () => {
  const dietProfile = useSelector(wnUserDietProfileSelector);

  const updateProfile = useUpdateProfile();

  const form = useForm({
    defaultValues: {
      numberOfDailyMeals: dietProfile?.numberOfDailyMeals ?? 0,
      mealSchedule: dietProfile?.mealSchedule ?? [],
      diets: dietProfile?.diets ?? [],
      allergies: dietProfile?.allergies ?? [],
      intolerances: dietProfile?.intolerances ?? [],
      supplements: dietProfile?.supplements.suplements ?? [],
      usesSupplements: dietProfile?.supplements.usesSupplements ?? false,
    },
  });

  const onSubmitHandler = form.handleSubmit(data => {
    const payload = {
      dietProfile: {
        numberOfDailyMeals: data.numberOfDailyMeals,
        mealSchedule: data.mealSchedule,
        diets: data.diets,
        allergies: data.allergies,
        intolerances: data.intolerances,
        supplements: {
          usesSupplements: data.usesSupplements,
          suplements: data.usesSupplements
            ? data.supplements.map(supplement => ({
                name: supplement.name,
                dose: supplement.dose,
                reason: supplement.reason,
                note: supplement.note,
              }))
            : [],
        },
      },
    };
    updateProfile.mutate(payload);
  });

  const onUpdateDailyMealsHandler = () => {
    const dailyMeals = form.getValues('numberOfDailyMeals');
    const mealSchedule = new Array(+dailyMeals).fill({
      hour: '00:00',
    });
    form.setValue('mealSchedule', mealSchedule);
  };

  const [mealSchedule, usesSupplements] = form.watch([
    'mealSchedule',
    'usesSupplements',
  ]);

  const classes = useStyles();

  return (
    <FormProvider {...form}>
      <ThemeProvider theme={theme => generateTheme(theme, 'light')}>
        <form onSubmit={onSubmitHandler}>
          <Box className={classes.stack} mb={4}>
            <Box component="section" className={classes.stack}>
              <Typography
                variant="h6"
                color="primary"
                style={{
                  color: 'black',
                  textTransform: 'uppercase',
                  paddingBottom: 16,
                }}
              >
                Información general sobre ingesta de comida
              </Typography>
              <Typography variant="body2">
                Número promedio de comidas al día
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={4}>
                  <Controller
                    control={form.control}
                    name="numberOfDailyMeals"
                    render={({ field: { value, onChange } }) => (
                      <NumberFormat
                        fullWidth
                        label="Comidas"
                        color="secondary"
                        variant="outlined"
                        customInput={TextField}
                        value={value}
                        onChange={onChange}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onUpdateDailyMealsHandler}
                  >
                    Actualizar
                  </Button>
                </Grid>
              </Grid>
              {!isEmpty(mealSchedule) && (
                <Box>
                  <Typography
                    variant="h6"
                    style={{ paddingBottom: 16, color: 'gray' }}
                  >
                    Horario de comidas
                  </Typography>
                  <Grid container spacing={4}>
                    {mealSchedule.map((schedule, index) => (
                      <Grid item xs={2} key={nanoid()}>
                        <Controller
                          name={`mealSchedule.${index}.hour`}
                          control={form.control}
                          render={({ field }) => (
                            <TextField
                              fullWidth
                              label={`Comida ${index + 1}`}
                              type="time"
                              value={field.value}
                              onChange={field.onChange}
                              InputLabelProps={{
                                shrink: true,
                              }}
                            />
                          )}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
            <Box component="section">
              <Typography
                variant="h6"
                color="primary"
                style={{
                  color: 'black',
                  textTransform: 'uppercase',
                  paddingBottom: 16,
                }}
              >
                Tipo de dieta
              </Typography>
              <Typography variant="body2" style={{ paddingBottom: 16 }}>
                Elige una o varias opciones.
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Controller
                    name="diets"
                    control={form.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel id="dietsLabel">Dietas</InputLabel>
                        <Select
                          fullWidth
                          multiple
                          labelId="dietsLabel"
                          value={field.value}
                          onChange={field.onChange}
                          color="secondary"
                          renderValue={selected => (
                            <div className={classes.chips}>
                              {selected.map(value => (
                                <Chip
                                  key={value}
                                  label={value}
                                  className={classes.chip}
                                />
                              ))}
                            </div>
                          )}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                              },
                            },
                          }}
                        >
                          {DIETS.map(diet => (
                            <MenuItem key={nanoid()} value={diet}>
                              {diet}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box component="section">
              <Typography
                variant="h6"
                color="primary"
                style={{
                  color: 'black',
                  textTransform: 'uppercase',
                  paddingBottom: 16,
                }}
              >
                Alergias
              </Typography>
              <Typography variant="body2" style={{ paddingBottom: 16 }}>
                Elige una o varias opciones.
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Controller
                    name="allergies"
                    control={form.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel id="allergiesLabel">Alergias</InputLabel>
                        <Select
                          fullWidth
                          multiple
                          labelId="allergiesLabel"
                          value={field.value}
                          onChange={field.onChange}
                          color="secondary"
                          renderValue={selected => (
                            <div className={classes.chips}>
                              {selected.map(value => (
                                <Chip
                                  key={value}
                                  label={value}
                                  className={classes.chip}
                                />
                              ))}
                            </div>
                          )}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                              },
                            },
                          }}
                        >
                          {ALLERGIES.map(allergy => (
                            <MenuItem key={nanoid()} value={allergy}>
                              {allergy}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box component="section">
              <Typography
                variant="h6"
                color="primary"
                style={{
                  color: 'black',
                  textTransform: 'uppercase',
                  paddingBottom: 16,
                }}
              >
                Intolerancias o restricciones alimentarias
              </Typography>
              <Typography variant="body2" style={{ paddingBottom: 16 }}>
                Elige una o varias opciones.
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Controller
                    name="intolerances"
                    control={form.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel id="intolerancesLabel">
                          Intolerancias
                        </InputLabel>
                        <Select
                          fullWidth
                          multiple
                          labelId="intolerancesLabel"
                          value={field.value}
                          onChange={field.onChange}
                          color="secondary"
                          renderValue={selected => (
                            <div className={classes.chips}>
                              {selected.map(value => (
                                <Chip
                                  key={value}
                                  label={value}
                                  className={classes.chip}
                                />
                              ))}
                            </div>
                          )}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                              },
                            },
                          }}
                        >
                          {INTOLERANCES.map(item => (
                            <MenuItem key={nanoid()} value={item}>
                              {item}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box component="section">
              <Typography
                variant="h6"
                color="primary"
                style={{
                  color: 'black',
                  textTransform: 'uppercase',
                  paddingBottom: 16,
                }}
              >
                Suplementos
              </Typography>
              <Box className={classes.stack}>
                <Controller
                  name="usesSupplements"
                  control={form.control}
                  render={({ field }) => (
                    <FormControlLabel
                      label="¿El paciente toma algún suplemento?"
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                          color="secondary"
                        />
                      }
                    />
                  )}
                />
                {usesSupplements && <SupplementsTable />}
              </Box>
            </Box>
          </Box>
          <Button
            type="submit"
            variant="contained"
            disabled={updateProfile.isLoading}
          >
            Guardar
          </Button>
        </form>
      </ThemeProvider>
    </FormProvider>
  );
};
